import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, ChefHat, Copy, Clock, Users, Layers, Printer, Check, X, Scale, TrendingUp, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { matches } from "@/lib/search";
import { brl, pct, num } from "@/lib/format";
import { cmvTone } from "@/lib/recipeCalc";
import { useAuth } from "@/context/AuthContext";
import { Button, Chip, EmptyState, ErrorState, Input, Loading, Page, PageHeader } from "@/components/kit";
import PrintDialog from "@/components/recipes/PrintDialog";
import RepriceDialog, { needsReprice } from "@/components/recipes/RepriceDialog";
import { CategoryChips } from "@/components/recipes/CategoryChips";
import MenuPdfDialog from "@/components/recipes/MenuPdfDialog";
import { sortByMenuOrder, useMenuCategories } from "@/lib/menuOrder";

const TONE_LABEL = { success: "Dentro da meta", warning: "Atenção", danger: "CMV alto", muted: "Sem preço" };
const toneVar = (t) => `var(--${t === "muted" ? "muted" : t === "warning" ? "accent" : t})`;

function CardFinance({ r, tone }) {
  if (r.is_base && !r.sale_price) {
    return (
      <div className="grid grid-cols-2 gap-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
        <div><div className="caption">Custo/porção</div><div className="label tnum">{brl(r.cost_per_portion)}</div></div>
        <div className="text-right"><div className="caption">{r.yield_qty ? `Custo/${r.yield_unit}` : "Custo total"}</div><div className="label tnum">{brl(r.yield_qty ? r.yield_unit_cost : r.total_cost)}</div></div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
      <div><div className="caption">Venda</div><div className="label tnum">{brl(r.sale_price)}</div><div className="caption tnum" data-testid={`recipe-suggested-${r.id}`}>Sug. {brl(r.suggested_price)}</div></div>
      <div><div className="caption">Custo/porção</div><div className="label tnum">{brl(r.cost_per_portion)}</div></div>
      <div className="text-right"><div className="caption">{TONE_LABEL[tone]}</div><div className="label tnum" style={{ color: toneVar(tone) }}>{pct(r.cmv_pct)}</div></div>
    </div>
  );
}

function RecipeCard({ r, isMgmt, onDuplicate, selecting, selected, onToggle }) {
  const nav = useNavigate();
  const tone = cmvTone(r.cmv_pct, r.cmv_target);
  const open = () => (selecting ? onToggle(r.id) : nav(isMgmt ? `/recipes/${r.id}` : `/recipes/${r.id}/kitchen`));
  return (
    <div className="card cursor-pointer flex flex-col gap-2" style={selected ? { outline: "2px solid var(--brand)" } : undefined} onClick={open} data-testid={`recipe-card-${r.id}`}>
      <div className="flex items-start gap-2">
        {selecting && <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 border" style={{ background: selected ? "var(--brand)" : "var(--surface)", borderColor: "var(--border)", color: "#fff" }} data-testid={`recipe-select-${r.id}`}>{selected && <Check size={14} />}</span>}
        <div className="flex-1 min-w-0"><div className="h3 truncate">{r.name}</div><div className="caption truncate">{r.menu_category || "Sem categoria"}</div></div>
        {isMgmt && !(r.is_base && !r.sale_price) && <span className={`badge badge-${tone} shrink-0`} data-testid={`recipe-cmv-${r.id}`}>CMV {pct(r.cmv_pct)}</span>}
        {isMgmt && !selecting && <button className="icon-btn shrink-0" title="Duplicar" onClick={(e) => { e.stopPropagation(); onDuplicate(r.id); }} data-testid={`recipe-duplicate-${r.id}`}><Copy size={14} /></button>}
      </div>
      <div className="flex flex-wrap items-center gap-3 caption">
        {r.is_base && <span className="badge badge-brand !py-0" data-testid={`recipe-base-badge-${r.id}`}><Layers size={11} /> Preparo base</span>}
        {r.prep_time_min ? <span className="flex items-center gap-1"><Clock size={12} /> {r.prep_time_min} min</span> : null}
        <span className="flex items-center gap-1"><Users size={12} /> {num(r.yield_portions)} porç.</span>
        <span>{r.ingredients.length} insumos</span>
      </div>
      {isMgmt && <CardFinance r={r} tone={tone} />}
    </div>
  );
}

function SelectBar({ list, selected, setSelected, onPrint, onCancel }) {
  const all = list.length > 0 && list.every((r) => selected.includes(r.id));
  return (
    <div className="card card-soft flex flex-wrap items-center gap-3 mb-4 sticky top-2 z-10" data-testid="recipe-select-bar">
      <span className="label flex-1" data-testid="recipe-selected-count">{selected.length} selecionada(s)</span>
      <Button size="sm" variant="ghost" onClick={() => setSelected(all ? [] : list.map((r) => r.id))} data-testid="recipe-select-all-button">{all ? "Limpar" : "Selecionar todas"}</Button>
      <Button size="sm" icon={Printer} disabled={!selected.length} onClick={onPrint} data-testid="recipe-print-selected-button">Imprimir</Button>
      <button className="icon-btn" onClick={onCancel} title="Cancelar" data-testid="recipe-select-cancel-button"><X size={16} /></button>
    </div>
  );
}

function HeaderActions({ isMgmt, selecting, onSelect, onMenu }) {
  const nav = useNavigate();
  return (
    <div className="flex gap-2">
      {isMgmt && <button className="icon-btn" title="CMV teórico x real" onClick={() => nav("/cmv-analysis")} data-testid="open-cmv-analysis-button"><Scale size={17} /></button>}
      {isMgmt && <button className="icon-btn" title="Cardápio em PDF" onClick={onMenu} data-testid="open-menu-pdf-button"><BookOpen size={17} /></button>}
      {!selecting && <button className="icon-btn" title="Imprimir várias" onClick={onSelect} data-testid="recipe-print-mode-button"><Printer size={17} /></button>}
      {isMgmt && <Button icon={Plus} onClick={() => nav("/recipes/new")} data-testid="new-recipe-button"><span className="hidden sm:inline">Nova ficha</span></Button>}
    </div>
  );
}

export default function Recipes() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isMgmt } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [menuPdf, setMenuPdf] = useState(false);
  const { data: raw = [], isLoading, isError, error, refetch } = useQuery({ queryKey: ["recipes"], queryFn: () => api("/recipes") });
  const { data: menuCats = [] } = useMenuCategories();
  const data = useMemo(() => sortByMenuOrder(raw, menuCats), [raw, menuCats]);
  const dup = useMutation({ mutationFn: (id) => api(`/recipes/${id}/duplicate`, { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["recipes"] }); toast.success("Ficha duplicada"); }, onError: (e) => toast.error(e.message) });
  const cats = useMemo(() => {
    const used = new Set(data.filter((r) => !r.is_base && r.menu_category).map((r) => r.menu_category.toLowerCase()));
    return menuCats.filter((c) => used.has(c.name.toLowerCase()));
  }, [data, menuCats]);
  const hasBase = data.some((r) => r.is_base);
  const list = data.filter((r) => (cat === "__base" ? r.is_base : !r.is_base && (cat === "all" || (r.menu_category || "").toLowerCase() === cat.toLowerCase())) && matches(r.name, q));
  const high = isMgmt ? data.filter((r) => cmvTone(r.cmv_pct, r.cmv_target) === "danger").length : 0;
  const below = isMgmt ? data.filter(needsReprice).length : 0;
  const [repricing, setRepricing] = useState(false);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const cancel = () => { setSelecting(false); setSelected([]); };
  return (
    <Page>
      <PageHeader title="Fichas técnicas" subtitle="Receituário operacional e CMV teórico dos pratos" right={<HeaderActions isMgmt={isMgmt} selecting={selecting} onSelect={() => setSelecting(true)} onMenu={() => setMenuPdf(true)} />} />
      {high > 0 && <div className="rounded-xl p-3 mb-4 label" style={{ background: "rgba(184,58,58,0.1)", color: "var(--danger)" }} data-testid="recipes-high-cmv-alert">{high} prato(s) com CMV teórico acima da meta + 5 p.p.</div>}
      {isMgmt && below > 0 && (
        <div className="card card-soft flex flex-wrap items-center gap-3 mb-4" data-testid="reprice-banner">
          <TrendingUp size={18} style={{ color: "var(--brand)" }} />
          <span className="label flex-1">{below} prato(s) abaixo do preço sugerido pela meta de CMV</span>
          <Button size="sm" onClick={() => setRepricing(true)} data-testid="open-reprice-button">Reajustar em lote</Button>
        </div>
      )}
      {selecting && <SelectBar list={list} selected={selected} setSelected={setSelected} onPrint={() => setPrinting(true)} onCancel={cancel} />}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 muted" />
        <Input className="pl-10" placeholder="Buscar prato" value={q} onChange={(e) => setQ(e.target.value)} data-testid="recipe-search-input" />
      </div>
      {(cats.length > 0 || hasBase) && (
        <div className="scroll-x mb-5" data-testid="recipe-category-chips">
          <Chip active={cat === "all"} onClick={() => setCat("all")} data-testid="recipe-category-all">Todas</Chip>
          {hasBase && <Chip active={cat === "__base"} onClick={() => setCat("__base")} data-testid="recipe-category-base"><Layers size={13} /> Preparos base</Chip>}
          <CategoryChips cats={cats} current={cat} onPick={setCat} canSort={isMgmt} />
        </div>
      )}
      {isLoading ? <Loading /> : isError ? <ErrorState message={error.message} onRetry={refetch} /> : !data.length ? (
        <EmptyState title="Nenhuma ficha técnica" text={isMgmt ? "Monte as receitas dos pratos com os insumos cadastrados e veja o CMV teórico." : "Ainda não há fichas técnicas cadastradas."}
          action={isMgmt && <Button icon={ChefHat} onClick={() => nav("/recipes/new")} data-testid="empty-new-recipe-button">Criar ficha</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="recipes-grid">
          {list.map((r) => <RecipeCard key={r.id} r={r} isMgmt={isMgmt} onDuplicate={(id) => dup.mutate(id)} selecting={selecting} selected={selected.includes(r.id)} onToggle={toggle} />)}
          {!list.length && <p className="caption">Nenhum prato encontrado.</p>}
        </div>
      )}
      {!isMgmt && data.length > 0 && !selecting && <p className="caption mt-4">Toque em um prato para ver o modo cozinha</p>}
      <PrintDialog open={printing} onOpenChange={setPrinting} recipes={data.filter((r) => selected.includes(r.id))} />
      {isMgmt && <MenuPdfDialog open={menuPdf} onOpenChange={setMenuPdf} recipes={data} />}
      {isMgmt && <RepriceDialog open={repricing} onOpenChange={setRepricing} recipes={data} />}
    </Page>
  );
}
